import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as es6printJS from 'print-js';
import { WatermarkConfig, PrintOutputOptions } from './print-options';

interface jsPDFWithPlugin extends jsPDF {
  autoTable: (options: any) => jsPDF;
  lastAutoTable: { finalY: number };
}

@Injectable({ providedIn: 'root' })
export class PrintService {

  createDoc(orientation: 'p' | 'l' = 'p', unit: any = 'mm', format: any = [210, 297]): jsPDFWithPlugin {
    return new jsPDF({ orientation, unit, format }) as jsPDFWithPlugin;
  }

  addWatermark(doc: jsPDFWithPlugin, config: WatermarkConfig): void {
    const text = config.text || 'DUPLICATA';
    const opacity = config.opacity ?? 0.15;
    const angle = config.angle ?? -45;
    const size = config.size || 60;
    const color = config.color || [150, 150, 150];

    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();

    doc.saveGraphicsState();
    doc.setGState(new (doc as any).GState({ opacity }));
    doc.setTextColor(color[0], color[1], color[2]);
    doc.setFontSize(size);
    doc.setFont('helvetica', 'bold');

    const cx = pageWidth / 2;
    const cy = config.positionY !== undefined ? config.positionY : pageHeight / 2;
    doc.text(text, cx, cy, {
      angle,
      align: 'center',
    } as any);
    doc.restoreGraphicsState();
  }

  addAgencyHeader(doc: jsPDFWithPlugin, agency: any, x: number, y: number, unit: string = 'mm'): number {
    let currentY = y;
    if (agency && agency.name) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text(agency.name, x, currentY);
      currentY += unit === 'mm' ? 6 : 0.2;
    }
    if (agency && agency.address) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(agency.address, x, currentY);
      currentY += unit === 'mm' ? 5 : 0.18;
    }
    if (agency && agency.phone) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Tel: ${agency.phone}`, x, currentY);
      currentY += unit === 'mm' ? 5 : 0.18;
    }
    return currentY;
  }

  addLogo(doc: jsPDFWithPlugin, logoBase64: string, x: number, y: number, width: number = 30, height: number = 30): void {
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', x, y, width, height);
      } catch {
        try {
          doc.addImage(logoBase64, 'PNG', x, y, width, height);
        } catch {
          // logo failed to load, skip silently
        }
      }
    }
  }

  addSignatureFields(doc: jsPDFWithPlugin, y: number, unit: string = 'mm', signatures?: string[]): void {
    const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
    const margin = unit === 'mm' ? 15 : 0.5;
    const list = signatures || ["Visa de l'Agent", 'Visa du Chef', 'Visa du Chauffeur', 'Visa Motoboy'];
    const colWidth = (pageWidth - margin * 2) / list.length;

    let targetY = y;
    if (targetY + (unit === 'mm' ? 25 : 1) > pageHeight) {
      doc.addPage();
      targetY = unit === 'mm' ? 25 : 1;
    }

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    list.forEach((sig, i) => {
      doc.text(sig, margin + colWidth * i + colWidth * 0.1, targetY);
      doc.line(margin + colWidth * i + colWidth * 0.1, targetY + 2, margin + colWidth * i + colWidth * 0.7, targetY + 2);
    });
  }

  getBase64ImageFromUrl(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!url || url === '') {
        resolve('');
        return;
      }
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
        }
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = (err) => {
        resolve('');
      };
    });
  }

  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
  outputPDF(doc: jsPDFWithPlugin, options?: PrintOutputOptions): void {
    const method = options?.method || 'autoprint';
    if (method === 'printjs') {
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      const printConfig: any = { printable: pdfBase64, type: 'pdf', base64: true };
      if (options?.onClose) {
        printConfig.onPrintDialogClose = options.onClose;
      }
      es6printJS(printConfig);
    } else {
      doc.autoPrint();
      window.open(doc.output('bloburl').toString(), '_blank');
      if (options?.onClose) {
        options.onClose();
      }
    }
  }
}
